import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  bulkCreateLocations,
  createLocation,
  deleteLocation,
  listLocations,
  renameLocation,
  type LevelType,
  type LocationNode,
  type ProjectCapabilities,
} from '../api.js';

/**
 * The location tree — Building → Level → Zone → Room.
 *
 * Shown flattened with indentation rather than as a collapsible tree. A fit-out
 * project has tens of locations, not thousands, and a PM setting them up wants
 * to see the shape at a glance; collapsing would hide the very thing they came
 * to check.
 */

const LEVEL_LABELS: Readonly<Record<LevelType, string>> = {
  building: 'Building',
  level: 'Level',
  zone: 'Zone',
  room: 'Room',
};

/** Coarse to fine. The order is the hierarchy. */
const LEVEL_ORDER: readonly LevelType[] = ['building', 'level', 'zone', 'room'];

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tree: readonly LocationNode[] }
  | { readonly status: 'not_permitted' }
  | { readonly status: 'failed'; readonly message: string };

interface Props {
  readonly projectId: string;
  readonly can: ProjectCapabilities;
}

export function Locations({ projectId, can }: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Which form is open: an id means "add under this one". */
  const [adding, setAdding] = useState<string | null | undefined>(undefined);
  const [bulk, setBulk] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', tree: await listLocations(projectId) });
    } catch (caught) {
      const denied = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setState(
        denied
          ? { status: 'not_permitted' }
          : { status: 'failed', message: 'Could not load the locations.' },
      );
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly LocationNode[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setState({ status: 'ready', tree: await work() });
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Locations</h3>
      {can.add_location && state.status === 'ready' && adding === undefined && !bulk && (
        <div className="head-actions">
          <button type="button" className="inline secondary" onClick={() => setBulk(true)}>
            Bulk create
          </button>
          <button type="button" className="inline" onClick={() => setAdding(null)}>
            Add location
          </button>
        </div>
      )}
    </div>
  );

  if (state.status === 'loading') {
    return (
      <>
        {head}
        <p aria-live="polite">Loading locations…</p>
      </>
    );
  }

  if (state.status === 'not_permitted') {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project's locations.</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {head}
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </>
    );
  }

  const flat = flatten(state.tree);

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {bulk && (
        <BulkForm
          busy={busy}
          parents={flat}
          onCancel={() => {
            setBulk(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => bulkCreateLocations(projectId, input))) setBulk(false);
          }}
        />
      )}

      {adding !== undefined && (
        <AddForm
          busy={busy}
          parentId={adding}
          parents={flat}
          onCancel={() => {
            setAdding(undefined);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => createLocation(projectId, input))) setAdding(undefined);
          }}
        />
      )}

      {flat.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No locations yet.</strong>
          </p>
          {can.add_location ? (
            <p>
              Set up the building, levels and zones so work, snags and photos can be pinned to a
              place. <strong>Bulk create</strong> does whole floors at once.
            </p>
          ) : (
            /*
             * An empty state must name an action ITS reader can take — or, when
             * they can take none, say why they are looking at nothing.
             *
             * This one told every reader to "set up the building" and pointed at
             * a Bulk create button only `location: create` draws. The owner
             * found it signed in as his own client: an instruction to configure
             * the contractor's project, shown to the customer.
             */
            <p>
              Nobody has set the building out yet. Once the levels and zones are added, work,
              snags and photos start showing which place they belong to.
            </p>
          )}
        </div>
      ) : (
        <ul className="tree">
          {flat.map((node) =>
            editing === node.id ? (
              <EditRow
                key={node.id}
                node={node}
                busy={busy}
                onCancel={() => setEditing(null)}
                onSubmit={async (input) => {
                  if (await run(() => renameLocation(projectId, node.id, input))) setEditing(null);
                }}
              />
            ) : (
              <li key={node.id} className="tree-row" style={{ paddingInlineStart: `${node.depth * 22}px` }}>
                <span className={`chip chip-${node.level_type}`}>
                  {LEVEL_LABELS[node.level_type]}
                </span>
                <span className="tree-name">{node.name}</span>
                {node.code && <span className="tree-code mono">{node.code}</span>}

                <span className="row-actions">
                  {can.add_location && node.level_type !== 'room' && (
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setBulk(false);
                        setAdding(node.id);
                      }}
                    >
                      Add inside<span className="visually-hidden"> {node.name}</span>
                    </button>
                  )}
                  {can.edit_location && (
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() => {
                        setError(null);
                        setEditing(node.id);
                      }}
                    >
                      Rename<span className="visually-hidden"> {node.name}</span>
                    </button>
                  )}
                  {can.remove_location && (
                    <button
                      type="button"
                      className="link-button danger"
                      disabled={busy}
                      onClick={() => void run(() => deleteLocation(projectId, node.id))}
                    >
                      Delete<span className="visually-hidden"> {node.name}</span>
                    </button>
                  )}
                </span>
              </li>
            ),
          )}
        </ul>
      )}

      {flat.length > 0 && (
        <p className="hint">
          {flat.length} location{flat.length === 1 ? '' : 's'}. Deleting one removes everything
          inside it.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface FlatNode extends LocationNode {
  readonly depth: number;
}

/** Depth-first, carrying how deep each node sits so the list can indent. */
function flatten(tree: readonly LocationNode[], depth = 0): FlatNode[] {
  const out: FlatNode[] = [];
  for (const node of tree) {
    out.push({ ...node, depth });
    out.push(...flatten(node.children, depth + 1));
  }
  return out;
}

/** The kinds that may sit inside this parent — strictly finer than it. */
function typesUnder(parent: FlatNode | undefined): readonly LevelType[] {
  if (!parent) return LEVEL_ORDER;
  return LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(parent.level_type) + 1);
}

// ---------------------------------------------------------------------------

interface AddProps {
  readonly busy: boolean;
  readonly parentId: string | null;
  readonly parents: readonly FlatNode[];
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    parent_id: string | null;
    level_type: LevelType;
    name: string;
    code?: string;
  }) => void | Promise<void>;
}

function AddForm({ busy, parentId, parents, onCancel, onSubmit }: AddProps): JSX.Element {
  const [parent, setParent] = useState<string>(parentId ?? '');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const parentNode = parents.find((p) => p.id === parent);
  const allowed = typesUnder(parentNode);
  const [levelType, setLevelType] = useState<LevelType>(allowed[0] ?? 'building');

  // Only offer kinds that can actually go there. The server refuses a level
  // inside a zone; a dropdown that offers it invites the error.
  const effectiveType = allowed.includes(levelType) ? levelType : (allowed[0] ?? 'room');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim()) return;
    void onSubmit({
      parent_id: parent || null,
      level_type: effectiveType,
      name: name.trim(),
      ...(code.trim() ? { code: code.trim() } : {}),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="loc-parent">Inside</label>
          <select
            id="loc-parent"
            value={parent}
            onChange={(e) => setParent(e.target.value)}
            disabled={busy}
          >
            <option value="">Nothing — top level</option>
            {parents
              .filter((p) => p.level_type !== 'room')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.path}
                </option>
              ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="loc-type">Kind</label>
          <select
            id="loc-type"
            value={effectiveType}
            onChange={(e) => setLevelType(e.target.value as LevelType)}
            disabled={busy || allowed.length === 0}
          >
            {allowed.map((type) => (
              <option key={type} value={type}>
                {LEVEL_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="loc-name">Name</label>
          <input
            id="loc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            disabled={busy}
            placeholder="Level 3"
          />
        </div>
        <div className="field">
          <label htmlFor="loc-code">Code (optional)</label>
          <input
            id="loc-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={40}
            disabled={busy}
            placeholder="L03"
          />
        </div>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          {busy ? 'Adding…' : 'Add location'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface BulkProps {
  readonly busy: boolean;
  readonly parents: readonly FlatNode[];
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    parent_id: string | null;
    levels: readonly { level_type: LevelType; name_pattern: string; from: number; to: number }[];
  }) => void | Promise<void>;
}

/**
 * "Levels 1-5, 4 zones each" — the whole reason this screen exists.
 *
 * Two rungs is what the real request is; a third is available but rarely
 * wanted, so it starts switched off rather than cluttering the form.
 */
function BulkForm({ busy, parents, onCancel, onSubmit }: BulkProps): JSX.Element {
  const [parent, setParent] = useState('');
  const [outerType, setOuterType] = useState<LevelType>('level');
  const [outerPattern, setOuterPattern] = useState('Level {n}');
  const [outerFrom, setOuterFrom] = useState('1');
  const [outerTo, setOuterTo] = useState('5');
  const [inner, setInner] = useState(true);
  const [innerType, setInnerType] = useState<LevelType>('zone');
  const [innerPattern, setInnerPattern] = useState('Zone {a}');
  const [innerCount, setInnerCount] = useState('4');

  const parentNode = parents.find((p) => p.id === parent);
  const outerAllowed = typesUnder(parentNode);
  const effectiveOuter = outerAllowed.includes(outerType) ? outerType : (outerAllowed[0] ?? 'room');
  const innerAllowed = LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(effectiveOuter) + 1);
  const effectiveInner = innerAllowed.includes(innerType) ? innerType : (innerAllowed[0] ?? 'room');

  const from = Number(outerFrom);
  const to = Number(outerTo);
  const count = Number(innerCount);
  const rangeOk = Number.isInteger(from) && Number.isInteger(to) && to >= from;
  const countOk = !inner || (Number.isInteger(count) && count >= 1);
  const total = rangeOk && countOk ? (to - from + 1) * (inner ? count + 1 : 1) : 0;
  const canSubmit = rangeOk && countOk && total > 0 && (!inner || innerAllowed.length > 0);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!canSubmit) return;
    void onSubmit({
      parent_id: parent || null,
      levels: [
        { level_type: effectiveOuter, name_pattern: outerPattern, from, to },
        ...(inner && innerAllowed.length > 0
          ? [{ level_type: effectiveInner, name_pattern: innerPattern, from: 1, to: count }]
          : []),
      ],
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Create a set of locations at once</p>

      <div className="field">
        <label htmlFor="bulk-parent">Inside</label>
        <select
          id="bulk-parent"
          value={parent}
          onChange={(e) => setParent(e.target.value)}
          disabled={busy}
        >
          <option value="">Nothing — top level</option>
          {parents
            .filter((p) => p.level_type !== 'room')
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.path}
              </option>
            ))}
        </select>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="bulk-outer-type">Kind</label>
          <select
            id="bulk-outer-type"
            value={effectiveOuter}
            onChange={(e) => setOuterType(e.target.value as LevelType)}
            disabled={busy}
          >
            {outerAllowed.map((t) => (
              <option key={t} value={t}>
                {LEVEL_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="bulk-outer-pattern">Named</label>
          <input
            id="bulk-outer-pattern"
            value={outerPattern}
            onChange={(e) => setOuterPattern(e.target.value)}
            maxLength={120}
            disabled={busy}
          />
          <p className="hint">
            <code>{'{n}'}</code> becomes the number, <code>{'{a}'}</code> a letter.
          </p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="bulk-from">From</label>
          <input
            id="bulk-from"
            value={outerFrom}
            onChange={(e) => setOuterFrom(e.target.value)}
            inputMode="numeric"
            disabled={busy}
            aria-invalid={!rangeOk}
          />
        </div>
        <div className="field">
          <label htmlFor="bulk-to">To</label>
          <input
            id="bulk-to"
            value={outerTo}
            onChange={(e) => setOuterTo(e.target.value)}
            inputMode="numeric"
            disabled={busy}
            aria-invalid={!rangeOk}
          />
        </div>
      </div>

      <div className="check">
        <input
          id="bulk-inner"
          type="checkbox"
          checked={inner}
          disabled={busy || innerAllowed.length === 0}
          onChange={(e) => setInner(e.target.checked)}
        />
        <label htmlFor="bulk-inner">And some inside each one</label>
      </div>

      {inner && innerAllowed.length > 0 && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="bulk-inner-type">Kind</label>
            <select
              id="bulk-inner-type"
              value={effectiveInner}
              onChange={(e) => setInnerType(e.target.value as LevelType)}
              disabled={busy}
            >
              {innerAllowed.map((t) => (
                <option key={t} value={t}>
                  {LEVEL_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="bulk-inner-pattern">Named</label>
            <input
              id="bulk-inner-pattern"
              value={innerPattern}
              onChange={(e) => setInnerPattern(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-inner-count">How many each</label>
            <input
              id="bulk-inner-count"
              value={innerCount}
              onChange={(e) => setInnerCount(e.target.value)}
              inputMode="numeric"
              disabled={busy}
              aria-invalid={!countOk}
            />
          </div>
        </div>
      )}

      <p className="hint" aria-live="polite">
        {canSubmit
          ? `This will create ${total} location${total === 1 ? '' : 's'}.`
          : 'Check the numbers — the range must count upwards.'}
      </p>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !canSubmit}>
          {busy ? 'Creating…' : `Create ${total || ''}`.trim()}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  readonly node: FlatNode;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { name: string; code: string | null }) => void | Promise<void>;
}

function EditRow({ node, busy, onCancel, onSubmit }: EditProps): JSX.Element {
  const [name, setName] = useState(node.name);
  const [code, setCode] = useState(node.code ?? '');

  return (
    <li className="tree-row editing" style={{ paddingInlineStart: `${node.depth * 22}px` }}>
      <span className={`chip chip-${node.level_type}`}>{LEVEL_LABELS[node.level_type]}</span>
      <label className="visually-hidden" htmlFor={`loc-name-${node.id}`}>
        Name for {node.name}
      </label>
      <input
        id={`loc-name-${node.id}`}
        className="tree-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={120}
        disabled={busy}
      />
      <label className="visually-hidden" htmlFor={`loc-code-${node.id}`}>
        Code for {node.name}
      </label>
      <input
        id={`loc-code-${node.id}`}
        className="tree-input narrow mono"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        maxLength={40}
        disabled={busy}
        placeholder="Code"
      />
      <span className="row-actions">
        <button
          type="button"
          className="link-button"
          disabled={busy || !name.trim()}
          onClick={() => void onSubmit({ name: name.trim(), code: code.trim() || null })}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </span>
    </li>
  );
}
